/**
 * Transport for the offline attendance routes of the End-User API
 * (`specs/offline-attendance-v1/implementation-plan.md`, "Shared wire contract
 * v1"; ERPat HumanResource `Attendance_sync_euapi`):
 *
 *   GET  /v1/api/me/attendance/offline-context?app_version=&client_schema=1 → {@link fetchOfflineContext}
 *   GET  /v1/api/me/attendance/sync/state                                    → {@link fetchSyncState}
 *   POST /v1/api/me/attendance/sync                                          → {@link postSync}
 *   GET  /v1/api/me/attendance/sync/events/{client_event_id}                 → {@link fetchReceipt}
 *
 * A raw `fetch` rather than the SDK, for two reasons: `ErpatClient.request()`
 * takes no custom headers, and every one of these routes is stamped with
 * `X-Installation-ID` — the offline installation uuid (decision D4), which ERPat
 * binds grants, sequences and the ingestion ledger to. It goes through
 * `sessionFetch()` so a sign-out cancels it, and mirrors what the SDK does
 * otherwise: the bearer + `X-Company-Key`, the `{ok, data}` / `{ok:false,
 * error}` envelope, and one refresh-and-retry on a 401 through the
 * single-flight `refreshSession()` — a refresh ERPat refuses ends the session,
 * one that merely did not get through does not (`expireAfterFailedRefresh()`).
 *
 * What this module does NOT do is decide anything. It returns ERPat's raw
 * `data` (the strict decoders in `contracts/offline-attendance-v1.ts` and the
 * sync engine judge it — HTTP 200 never implies acceptance) or throws an
 * {@link OfflineApiError} that classifies the failure:
 *
 *  - ERPat's refusal: its envelope `code` (`forbidden`, `account_inactive`,
 *    `module_unavailable`, `validation_failed`, `unsupported_schema`,
 *    `offline_sync_unavailable`, `rate_limited`, a 401 token code …) with the
 *    HTTP status, the envelope's `details` (kept raw — ERPat sends `[]` when
 *    empty) and the server's retry hint (`Retry-After`, `details.retry_after`),
 *    BOUNDED at {@link OFFLINE_RETRY_AFTER_MAX_MS} (one hour, plan amendment
 *    C16): an HTTP-date is measured against the device clock, which may be
 *    skewed — a day slow, it would read an honest date as a day away — and a
 *    proxy in front of ERPat can send anything, so no hint may park the queue
 *    longer than an hour. The sync engine bounds a deferred result's per-event
 *    `retry_after_ms` the same way, with {@link boundRetryAfterMs};
 *  - not ERPat's answer: `invalid_response` (a body that is not JSON — a web
 *    page, a captive portal, the `/install` redirect — or a 2xx without the
 *    envelope), `http_<status>` (a non-2xx with no usable envelope code);
 *  - no answer at all (status 0): `network_error`, `timeout` (the
 *    {@link OFFLINE_REQUEST_TIMEOUT_MS} ceiling), `security_challenge` (an edge
 *    check answered instead of ERPat — `challenge: true`), `cancelled` (a
 *    sign-out, or the caller's own signal — `cancelled: true`), `no_session`
 *    (no bearer or company key: nothing was sent);
 *  - the token: `refresh_failed` (status 0 — the refresh did not get through,
 *    the session lives on) and `session_expired` (the session ended or is
 *    ending — status 401 once its epoch moved, 0 when only a sign-out cancelled
 *    the refresh). An answer that arrives after the session's epoch moved is
 *    withheld as `session_expired` too, so nothing an old session asked for
 *    reaches the next one.
 *
 * Content-free like `realtime.api.ts`: an error's `message` IS its code;
 * ERPat's human `message` is never kept, logged or rethrown.
 */
import { OFFLINE_SCHEMA_VERSION, type SyncRequestV1 } from '@/contracts/offline-attendance-v1';
import { OFFLINE_REQUEST_TIMEOUT_MS } from '@/contracts/offline-attendance-policy';
import {
  erpatClient,
  getSessionEpoch,
  getStoredCompanyKey,
  onSessionCancel,
  sessionCancelledError,
  sessionFetch,
} from '@/services/api/erpat-client';
import { expireAfterFailedRefresh, isSessionCancelled, refreshSession } from '@/services/api/http';
import { isSecurityChallenge } from '@/utils/edge-challenge';
import { API_BASE_URL } from '@/utils/env';

const OFFLINE_CONTEXT_PATH = '/v1/api/me/attendance/offline-context';
const SYNC_STATE_PATH = '/v1/api/me/attendance/sync/state';
const SYNC_PATH = '/v1/api/me/attendance/sync';
const SYNC_EVENTS_PATH = '/v1/api/me/attendance/sync/events';

/** An envelope `code` worth keeping: what the contract decoder accepts as a code (1–64, no surrounding space). */
const MAX_CODE_LENGTH = 64;

/* ── Error ──────────────────────────────────────────────────────────────── */

export interface OfflineApiErrorOptions {
  /**
   * How long the server asked us to wait, in ms (`Retry-After`,
   * `details.retry_after`) — within [0, {@link OFFLINE_RETRY_AFTER_MAX_MS}] when
   * this module computed it.
   */
  retryAfterMs?: number | null;
  /** The envelope's `error.details`, raw (`[]` when ERPat had none). */
  details?: unknown;
  /** The request was cancelled locally — a sign-out or the caller's signal; not a failure. */
  cancelled?: boolean;
  /** An edge security check answered in ERPat's place (`isSecurityChallenge`). */
  challenge?: boolean;
}

/** A content-free failure of the offline attendance transport (see the module docblock). */
export class OfflineApiError extends Error {
  /** ERPat's envelope code, or one of this module's own (`network_error`, `timeout` …). */
  readonly code: string;
  /** The HTTP status, or `0` when no answer from ERPat was read. */
  readonly status: number;
  readonly retryAfterMs: number | null;
  readonly details: unknown;
  readonly cancelled: boolean;
  readonly challenge: boolean;

  constructor(code: string, status: number, opts: OfflineApiErrorOptions = {}) {
    super(code);
    this.name = 'OfflineApiError';
    this.code = code;
    this.status = status;
    this.retryAfterMs = opts.retryAfterMs ?? null;
    this.details = opts.details ?? null;
    this.cancelled = opts.cancelled ?? false;
    this.challenge = opts.challenge ?? false;
  }
}

/* ── Timing ─────────────────────────────────────────────────────────────── */

/**
 * A response's `data` plus the clocks around the exchange that produced it, for
 * `calibrate()` (`clock-health.ts`): device wall time and `performance.now()`
 * just before the request went out and just after its response arrived. After a
 * 401 refresh these describe the RETRY — never the refresh in between.
 */
export interface Timed<T> {
  data: T;
  sentDeviceMs: number;
  receivedDeviceMs: number;
  sentPerfMs: number;
  receivedPerfMs: number;
}

type Timing = Omit<Timed<unknown>, 'data'>;

/* ── Retry-After ────────────────────────────────────────────────────────── */

/**
 * The longest wait any retry hint may impose: one hour (plan amendment C16).
 * Past it a hint still means "wait" — the bound, never "no hint" — but it can
 * never stall the queue for a day because a device clock is skewed or a proxy
 * sent nonsense.
 */
export const OFFLINE_RETRY_AFTER_MAX_MS = 3_600_000;

/**
 * A retry hint in ms bounded to [0, {@link OFFLINE_RETRY_AFTER_MAX_MS}], rounded
 * up to a whole millisecond — or `null` when `value` is no hint at all (not a
 * number, `NaN`, negative). Every hint this module reports went through it; the
 * sync engine passes a deferred result's per-event `retry_after_ms` through it
 * too (the contract decoder accepts any finite, non-negative value).
 */
export function boundRetryAfterMs(value: unknown): number | null {
  if (typeof value !== 'number' || Number.isNaN(value) || value < 0) return null;
  return Math.min(OFFLINE_RETRY_AFTER_MAX_MS, Math.ceil(value));
}

const DAY_NAMES = '(?:Mon|Tue|Wed|Thu|Fri|Sat|Sun)';
const LONG_DAY_NAMES = '(?:Monday|Tuesday|Wednesday|Thursday|Friday|Saturday|Sunday)';
const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'] as const;
const MONTH = `(${MONTH_NAMES.join('|')})`;
const TIME = '(\\d{2}):(\\d{2}):(\\d{2})';

/** `Sun, 06 Nov 1994 08:49:37 GMT` (RFC 9110 IMF-fixdate — what every current server sends). */
const IMF_FIXDATE_RE = new RegExp(`^${DAY_NAMES}, (\\d{2}) ${MONTH} (\\d{4}) ${TIME} GMT$`);
/** `Sunday, 06-Nov-94 08:49:37 GMT` (obsolete RFC 850 form, two-digit year). */
const RFC850_DATE_RE = new RegExp(`^${LONG_DAY_NAMES}, (\\d{2})-${MONTH}-(\\d{2}) ${TIME} GMT$`);
/** `Sun Nov  6 08:49:37 1994` (obsolete asctime form, space-padded day). */
const ASCTIME_DATE_RE = new RegExp(`^${DAY_NAMES} ${MONTH} ( \\d|\\d{2}) ${TIME} (\\d{4})$`);

/** Epoch ms of a UTC calendar instant, or `null` when any field rolls over (Feb 30, 24:00:00, a leap second). */
function utcInstant(year: number, month: number, day: number, hour: number, minute: number, second: number): number | null {
  const ms = Date.UTC(year, month, day, hour, minute, second);
  const date = new Date(ms);
  return Number.isFinite(ms) &&
    date.getUTCFullYear() === year &&
    date.getUTCMonth() === month &&
    date.getUTCDate() === day &&
    date.getUTCHours() === hour &&
    date.getUTCMinutes() === minute &&
    date.getUTCSeconds() === second
    ? ms
    : null;
}

function monthIndex(name: string | undefined): number {
  return MONTH_NAMES.findIndex((month) => month === name);
}

/**
 * An HTTP-date in any of the three forms recipients must accept (RFC 9110
 * §5.6.7), as epoch ms; `null` for anything else. Parsed field by field —
 * never through `Date.parse`, which would read "7.5" or an ISO string as a date.
 */
function parseHttpDate(value: string, nowMs: number): number | null {
  let match = IMF_FIXDATE_RE.exec(value);
  if (match) {
    const [, day, month, year, hour, minute, second] = match;
    return utcInstant(Number(year), monthIndex(month), Number(day), Number(hour), Number(minute), Number(second));
  }
  match = RFC850_DATE_RE.exec(value);
  if (match) {
    const [, day, month, shortYear, hour, minute, second] = match;
    // A two-digit year more than 50 years ahead means the century before (RFC 9110 §5.6.7).
    let year = 2000 + Number(shortYear);
    if (year - new Date(nowMs).getUTCFullYear() > 50) year -= 100;
    return utcInstant(year, monthIndex(month), Number(day), Number(hour), Number(minute), Number(second));
  }
  match = ASCTIME_DATE_RE.exec(value);
  if (match) {
    const [, month, day, hour, minute, second, year] = match;
    return utcInstant(Number(year), monthIndex(month), Number(day), Number(hour), Number(minute), Number(second));
  }
  return null;
}

/**
 * A `Retry-After` value as a delay in ms within [0,
 * {@link OFFLINE_RETRY_AFTER_MAX_MS}]: delay-seconds (`"7"` → 7000) or an
 * HTTP-date measured from `nowMs` — the device clock by default (a date in the
 * past → 0). A longer delay, however long, is the one-hour bound (C16). `null`
 * when absent or unreadable.
 */
export function parseRetryAfter(value: string | null, nowMs: number = Date.now()): number | null {
  if (typeof value !== 'string') return null;
  const text = value.trim();
  if (text === '') return null;
  if (/^\d+$/.test(text)) {
    return boundRetryAfterMs(Number(text) * 1000);
  }
  if (!Number.isFinite(nowMs)) return null;
  const at = parseHttpDate(text, nowMs);
  return at === null ? null : boundRetryAfterMs(Math.max(0, at - nowMs));
}

/**
 * `details.retry_after` — whole seconds on ERPat (`EndUserRateLimitMiddleware`),
 * a number or a digit string — in ms, bounded like {@link parseRetryAfter}.
 */
function detailsRetryAfterMs(details: unknown): number | null {
  if (!isRecord(details)) return null;
  const value = details.retry_after;
  if (typeof value === 'number') {
    return Number.isFinite(value) && value >= 0 ? boundRetryAfterMs(value * 1000) : null;
  }
  return typeof value === 'string' ? parseRetryAfter(value) : null;
}

/**
 * The longest wait any hint asks for — never retry earlier than the server
 * said, and (each hint being bounded) never later than an hour.
 */
function retryAfterOf(retryAfterHeader: string | null, details: unknown): number | null {
  const hints = [parseRetryAfter(retryAfterHeader), detailsRetryAfterMs(details)].filter(
    (hint): hint is number => hint !== null,
  );
  return hints.length > 0 ? Math.max(...hints) : null;
}

/* ── One exchange ───────────────────────────────────────────────────────── */

interface OfflineRequest {
  method: 'GET' | 'POST';
  path: string;
  installationId: string;
  query?: Readonly<Record<string, string>>;
  body?: SyncRequestV1;
  signal?: AbortSignal;
}

/** What one exchange produced, read in full (the body is small). */
interface Exchange {
  status: number;
  ok: boolean;
  retryAfter: string | null;
  text: string;
  timing: Timing;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function apiUrl(path: string, query?: Readonly<Record<string, string>>): string {
  const base = API_BASE_URL.replace(/\/+$/, '');
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query ?? {})) {
    if (value !== '') params.set(key, value);
  }
  const qs = params.toString();
  return `${base}${path}${qs ? `?${qs}` : ''}`;
}

/** The session's headers, or `null` when there is no session to send them for. */
function sessionHeaders(installationId: string, withJsonBody: boolean): Record<string, string> | null {
  const token = erpatClient.getAccessToken();
  const companyKey = getStoredCompanyKey() ?? erpatClient.getCompanyKey();
  // Without a tenant the request would reach whatever database the server
  // defaults to; the offline scope is bound to one company, so send nothing.
  if (!token || !companyKey) return null;
  const headers: Record<string, string> = {
    Accept: 'application/json',
    Authorization: `Bearer ${token}`,
    'X-Company-Key': companyKey,
    'X-Installation-ID': installationId,
  };
  if (withJsonBody) headers['Content-Type'] = 'application/json';
  return headers;
}

function cancelledError(): OfflineApiError {
  return new OfflineApiError('cancelled', 0, { cancelled: true });
}

/**
 * Send one request and read its whole body, under its own deadline. A sign-out
 * cancels it at ANY point — `sessionFetch()` covers the wait for headers, and
 * the canceller registered here covers the body read after them. The caller's
 * signal does the same.
 */
async function exchange(request: OfflineRequest): Promise<Exchange> {
  const headers = sessionHeaders(request.installationId, request.body !== undefined);
  if (!headers) throw new OfflineApiError('no_session', 0);
  const callerSignal = request.signal;
  if (callerSignal?.aborted) throw cancelledError();
  // Serialized before the clock starts: a body that cannot be serialized is a
  // programming error and surfaces as one, never as a transport failure.
  const body = request.body !== undefined ? JSON.stringify(request.body) : undefined;

  const controller = new AbortController();
  let timedOut = false;
  let signedOut = false;
  const timer = setTimeout(() => {
    timedOut = true;
    controller.abort(new DOMException('The offline attendance request timed out.', 'TimeoutError'));
  }, OFFLINE_REQUEST_TIMEOUT_MS);
  const onCallerAbort = (): void => controller.abort(callerSignal?.reason);
  callerSignal?.addEventListener('abort', onCallerAbort, { once: true });
  const untrack = onSessionCancel(() => {
    signedOut = true;
    controller.abort(sessionCancelledError());
  });

  /** Why this exchange produced no answer. */
  const noAnswer = (err: unknown): OfflineApiError => {
    if (signedOut || isSessionCancelled(err)) return cancelledError();
    if (isSecurityChallenge(err)) return new OfflineApiError('security_challenge', 0, { challenge: true });
    if (timedOut) return new OfflineApiError('timeout', 0);
    if (callerSignal?.aborted) return cancelledError();
    return new OfflineApiError('network_error', 0);
  };

  try {
    const sentDeviceMs = Date.now();
    const sentPerfMs = performance.now();
    let response: Response;
    try {
      response = await sessionFetch(apiUrl(request.path, request.query), {
        method: request.method,
        headers,
        body,
        cache: 'no-store',
        signal: controller.signal,
      });
    } catch (err) {
      // No response at all: classified, never rethrown raw.
      throw noAnswer(err);
    }
    const receivedDeviceMs = Date.now();
    const receivedPerfMs = performance.now();

    let text: string;
    try {
      text = await response.text();
    } catch (err) {
      // The headers arrived, the body did not: the same as no answer.
      throw noAnswer(err);
    }
    return {
      status: response.status,
      ok: response.ok,
      retryAfter: response.headers.get('Retry-After'),
      text,
      timing: { sentDeviceMs, receivedDeviceMs, sentPerfMs, receivedPerfMs },
    };
  } finally {
    clearTimeout(timer);
    callerSignal?.removeEventListener('abort', onCallerAbort);
    untrack();
  }
}

/** Resolve `{ok:true,data}` to `data`; throw everything else as an {@link OfflineApiError}. */
function readEnvelope(answer: Exchange): unknown {
  let parsed: unknown = null;
  if (answer.text.length > 0) {
    try {
      parsed = JSON.parse(answer.text);
    } catch {
      // Not JSON: something other than ERPat answered (a web page, a portal).
      throw new OfflineApiError('invalid_response', answer.status, {
        retryAfterMs: retryAfterOf(answer.retryAfter, null),
      });
    }
  }
  const envelope = isRecord(parsed) ? parsed : null;

  if (envelope !== null && envelope.ok === false) {
    const error = isRecord(envelope.error) ? envelope.error : {};
    const code =
      typeof error.code === 'string' &&
      error.code.length > 0 &&
      error.code.length <= MAX_CODE_LENGTH &&
      error.code.trim() === error.code
        ? error.code
        : answer.ok
          ? 'invalid_response'
          : `http_${answer.status}`;
    throw new OfflineApiError(code, answer.status, {
      retryAfterMs: retryAfterOf(answer.retryAfter, error.details),
      details: error.details,
    });
  }
  if (!answer.ok) {
    throw new OfflineApiError(`http_${answer.status}`, answer.status, {
      retryAfterMs: retryAfterOf(answer.retryAfter, null),
    });
  }
  if (envelope === null || envelope.ok !== true || !Object.prototype.hasOwnProperty.call(envelope, 'data')) {
    // A 2xx that is not ERPat's envelope proves nothing about the request.
    throw new OfflineApiError('invalid_response', answer.status);
  }
  return envelope.data;
}

function sessionEnded(): OfflineApiError {
  return new OfflineApiError('session_expired', 401);
}

/**
 * One request with the SDK's 401 semantics (refresh once, retry once), the
 * session-epoch guard around every await, and the envelope resolved.
 */
async function offlineRequest(request: OfflineRequest): Promise<{ data: unknown; timing: Timing }> {
  const epoch = getSessionEpoch();
  let answer = await exchange(request);
  if (getSessionEpoch() !== epoch) throw sessionEnded();

  if (answer.status === 401 && erpatClient.getRefreshToken()) {
    try {
      await refreshSession();
    } catch (err) {
      // Ends the session only on ERPat's own refusal of the refresh token —
      // not when a sign-out cancelled it, and not when it never got an answer.
      expireAfterFailedRefresh(err, epoch);
      const ended = getSessionEpoch() !== epoch;
      throw new OfflineApiError(ended || isSessionCancelled(err) ? 'session_expired' : 'refresh_failed', ended ? 401 : 0);
    }
    if (getSessionEpoch() !== epoch) throw sessionEnded();
    answer = await exchange(request);
    if (getSessionEpoch() !== epoch) throw sessionEnded();
  }

  return { data: readEnvelope(answer), timing: answer.timing };
}

/* ── Routes ─────────────────────────────────────────────────────────────── */

/**
 * `GET /me/attendance/offline-context` — provisioning: capability, the (reused
 * or new) grant, the JWKS, the policy, the confirmed snapshot and ERPat's
 * `server_time`, with the timings `calibrate()` needs. Decode `data` with
 * `decodeOfflineContext()`.
 */
export async function fetchOfflineContext(
  installationId: string,
  appVersion: string,
  signal?: AbortSignal,
): Promise<Timed<unknown>> {
  const { data, timing } = await offlineRequest({
    method: 'GET',
    path: OFFLINE_CONTEXT_PATH,
    installationId,
    query: { app_version: appVersion.trim(), client_schema: String(OFFLINE_SCHEMA_VERSION) },
    signal,
  });
  return { data, ...timing };
}

/**
 * `GET /me/attendance/sync/state` — the cheap confirmed-snapshot refresh (no
 * grant issuance). Decode `data` with `decodeSyncState()`.
 */
export async function fetchSyncState(installationId: string, signal?: AbortSignal): Promise<Timed<unknown>> {
  const { data, timing } = await offlineRequest({
    method: 'GET',
    path: SYNC_STATE_PATH,
    installationId,
    signal,
  });
  return { data, ...timing };
}

/**
 * `POST /me/attendance/sync` — upload one ordered batch. The `X-Installation-ID`
 * header is the body's own `installation_id`, so the two ERPat compares can
 * never disagree. Decode `data` with `decodeSyncResponse()`; a 200 settles
 * nothing by itself.
 */
export async function postSync(body: SyncRequestV1, signal?: AbortSignal): Promise<unknown> {
  const { data } = await offlineRequest({
    method: 'POST',
    path: SYNC_PATH,
    installationId: body.installation_id,
    body,
    signal,
  });
  return data;
}

/**
 * `GET /me/attendance/sync/events/{client_event_id}` — the owner-only receipt
 * lookup (another employee's id reads `not_found`). Decode `data` with
 * `decodeReceiptLookup()`.
 */
export async function fetchReceipt(
  installationId: string,
  clientEventId: string,
  signal?: AbortSignal,
): Promise<unknown> {
  const { data } = await offlineRequest({
    method: 'GET',
    path: `${SYNC_EVENTS_PATH}/${encodeURIComponent(clientEventId)}`,
    installationId,
    signal,
  });
  return data;
}
